<template>
  <q-dialog
    v-model="model"
    transition-show="scale"
    transition-hide="scale"
    aria-role="dialog"
    aria-modal
    v-bind="$attrs"
    backdrop-filter="blur(3px)"
    :maximized="$q.screen.xs"
  >
    <q-card
      :class="{ 'wide-dialog': wideDialog, 're-dialog': true }"
      class="column no-wrap"
    >
      <q-card-section
        v-if="hasCloseButton || title || headerIcon || $slots.title"
        class="column q-pa-none justify-between"
      >
        <div class="row justify-between bg-grey-2">
          <slot name="title">
            <div class="row re-ma items-center">
              <q-avatar
                v-if="headerIcon"
                size="md"
                :class="`text-${type} bg-${type}-bground q-mr-sm`"
                :icon="headerIcon"
              />
              <h5
                v-if="title"
                class="col-auto"
              >
                {{ title }}
              </h5>
            </div>
          </slot>
          <q-btn
            v-if="hasCloseButton"
            icon="mdi-close"
            size="xs"
            flat
            round
            outline
            class="text-black bg-transparent self-center q-mr-lg bg-grey-4"
            @click="handleClose"
          />
        </div>
        <q-separator />
      </q-card-section>
      <q-card-section class="main-content">
        <div
          :class="innerClass"
          class="q-pa-md column justify-center"
        >
          <slot />
        </div>
      </q-card-section>
      <template v-if="hasCancel || hasConfirm || onCancel || onConfirm || $slots.actions">
        <q-separator />
        <q-card-section>
          <!-- TODO: should we be using q-card-actions? -->
          <div
            :class="[$q.screen.xs ? 'column reverse' : 'row']"
            class="q-gutter-x-md q-gutter-y-sm justify-end xs-btn-full-width"
          >
            <re-button
              v-if="hasCancel || onCancel"
              size="md"
              :label="cancelLabel || t('monolith.shared.cancel')"
              :readonly="false"
              @click="handleCancel"
            />
            <slot name="actions" />
            <re-button
              v-if="hasConfirm || onConfirm"
              type="submit"
              :color="type || 'main'"
              :readonly="false"
              size="md"
              :disabled="!canConfirm"
              :label="confirmLabel || t('global.action.confirm')"
              @click="handleConfirm"
            />
          </div>
        </q-card-section>
      </template>
    </q-card>
  </q-dialog>
</template>
<script setup lang="ts">
import { onKeyStroke } from '@vueuse/core'
import { useQuasar } from 'quasar'
import { computed, useAttrs } from 'vue'
import { useI18n } from 'vue-i18n'

import enums from '@/plugins/enums'

const model = defineModel({
  type: Boolean,
  default: null,
})

const $q = useQuasar()
const { t } = useI18n()
const attrs = useAttrs()
const props = defineProps({
  title: {
    type: String,
    default: undefined,
  },
  hasCancel: {
    type: Boolean,
    default: false,
  },
  hasConfirm: {
    type: Boolean,
    default: false,
  },
  hasCloseButton: {
    type: Boolean,
    default: false,
  },
  onCancel: {
    type: Function,
    default: undefined,
  },
  cancelLabel: {
    type: String,
    default: undefined,
  },
  onConfirm: {
    type: Function,
    default: undefined,
  },
  confirmLabel: {
    type: String,
    default: undefined,
  },
  wideDialog: {
    type: Boolean,
    default: false,
  },
  headerIcon: {
    type: String,
    default: null,
  },
  innerClass: {
    type: String,
    default: '',
  },
  canConfirm: {
    type: Boolean,
    default: true,
  },
  type: {
    type: String,
    default: null,
    validator: (val: string | null | undefined) =>
      val == null || Object.values(enums.feedbackType).includes(val),
  },
})

const emit = defineEmits(['cancel', 'confirm', 'close'])

const handleClose = () => {
  model.value = false
  emit('close')
}

const handleCancel = () => {
  model.value = false
  emit('cancel')
}

const handleConfirm = () => {
  model.value = false
  emit('confirm')
}

enum KeyStrokes {
  Escape = 'Escape',
  Enter = 'Enter',
}

const isPersistent = computed(() => [true, ''].includes(attrs.persistent as string | boolean))

onKeyStroke([KeyStrokes.Escape, KeyStrokes.Enter], (e) => {
  // ensure that key strokes only trigger when dialog is open
  if (!model.value) return
  if (e.key === KeyStrokes.Escape) {
    if (isPersistent.value) return
    if (props.onCancel || props.hasCancel) {
      handleCancel()
    } else if (props.hasCloseButton) {
      handleClose()
    }
  }
  if (e.key === KeyStrokes.Enter) {
    if (props.onConfirm || props.hasConfirm) {
      handleConfirm()
    }
  }
})
</script>

<style scoped lang="scss">
.wide-dialog {
  max-width: 950px;
}

.re-dialog {
  min-width: 480px;

  .main-content {
    height: 100%;
    // max-height: 70vh;
    overflow: auto;
  }
}

body.screen--xs .re-dialog {
  min-width: inherit;
}
</style>
